import { useEffect, useMemo, useState } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '~/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog';
import { Label } from '~/components/ui/label';
import { RadioGroup, RadioGroupItem } from '~/components/ui/radio-group';
import { formatCurrency } from '~/utils/paymentUtils';
import type { SchoolInvoiceTableRow } from './invoiceRules';
import { usePostSchoolInvoices, type PostSchoolInvoicesFamily, type PostSchoolInvoicesPayload } from './usePostSchoolInvoices';

type CollectionMethod = 'send_invoice' | 'charge_automatically';

export interface PostSchoolInvoicesOutcome {
  payload: PostSchoolInvoicesPayload;
  collectionMethod: CollectionMethod;
  /** Invoice ids whose family was refused or whose row failed, for a retry. */
  failedInvoiceIds: string[];
}

interface PostSchoolInvoicesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schoolYearId: string;
  invoices: readonly SchoolInvoiceTableRow[];
  onCompleted: (outcome: PostSchoolInvoicesOutcome) => void;
}

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

// The rows a family-level refusal or a failed row leaves unposted. A family
// with a message posted nothing, so every one of its selected rows is still a
// draft; a family that went through may still have single failed rows.
function failedInvoiceIds(families: readonly PostSchoolInvoicesFamily[], selected: readonly SchoolInvoiceTableRow[]) {
  const ids = new Set<string>();
  for (const family of families) {
    if (family.message) {
      for (const row of selected) {
        if (row.familyEnrollmentId === family.familyEnrollmentId) ids.add(row.invoiceId);
      }
    }
    for (const row of family.rows) {
      if (row.action === 'failed') ids.add(row.invoiceId);
    }
  }
  return Array.from(ids);
}

export function PostSchoolInvoicesDialog({
  open,
  onOpenChange,
  schoolYearId,
  invoices,
  onCompleted,
}: PostSchoolInvoicesDialogProps) {
  const [collectionMethod, setCollectionMethod] = useState<CollectionMethod>('send_invoice');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<PostSchoolInvoicesPayload | null>(null);
  const [postSchoolInvoices, { loading }] = usePostSchoolInvoices();

  useEffect(() => {
    if (!open) return;
    setCollectionMethod('send_invoice');
    setError(null);
    setResult(null);
  }, [open]);

  const familyCount = useMemo(() => new Set(invoices.map((row) => row.familyEnrollmentId)).size, [invoices]);
  const totalCents = useMemo(() => invoices.reduce((sum, row) => sum + row.totalCents, 0), [invoices]);

  // Automatic collection is offered only when something selected can actually
  // be charged: tuition, from a family that authorized auto-pay. Under it, the
  // backend skips every other selected row and refuses a family without
  // authorization, so both are spelled out before the admin confirms.
  const chargeable = useMemo(() => invoices.filter((row) => row.autoCollectable), [invoices]);
  const offerAutomatic = chargeable.length > 0;
  const skippedUnderAutomatic = useMemo(() => invoices.filter((row) => !row.autoCollectable), [invoices]);
  const familiesWithoutAutopay = useMemo(() => {
    const names = new Map<string, string>();
    for (const row of invoices) {
      if (!row.autopayEnabled) names.set(row.familyEnrollmentId, row.familyName);
    }
    return Array.from(names.values()).sort((a, b) => a.localeCompare(b));
  }, [invoices]);

  const memoFor = useMemo(() => {
    const byId = new Map(invoices.map((row) => [row.invoiceId, row]));
    return (invoiceId: string) => {
      const row = byId.get(invoiceId);
      return row?.memo ?? 'Invoice';
    };
  }, [invoices]);

  const handleConfirm = async () => {
    setError(null);
    const response = await postSchoolInvoices({
      variables: {
        input: {
          schoolYearId,
          invoiceIds: invoices.map((row) => row.invoiceId),
          collectionMethod,
        },
      },
    });
    const payload = response.data?.postSchoolInvoices;
    if (!payload) {
      setError('Posting failed. Nothing was sent to Stripe.');
      return;
    }
    // A refusal posts nothing and names why; a partial run has families to show.
    if (payload.families.length === 0) {
      setError(payload.userErrors?.[0]?.message ?? 'Posting failed. Nothing was sent to Stripe.');
      return;
    }
    setResult(payload);
    onCompleted({
      payload,
      collectionMethod,
      failedInvoiceIds: failedInvoiceIds(payload.families, invoices),
    });
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!loading) onOpenChange(next); }}>
      <DialogContent className="sm:max-w-lg">
        {result ? (
          <>
            <DialogHeader>
              <DialogTitle>{result.success ? 'Posted' : 'Posted with problems'}</DialogTitle>
              <DialogDescription>
                {plural(result.pushedCount, 'invoice', 'invoices')} posted across{' '}
                {plural(result.familyCount, 'family', 'families')}.
              </DialogDescription>
            </DialogHeader>
            <ul className="max-h-80 space-y-3 overflow-y-auto text-sm" aria-label="Results by family">
              {result.families.map((family) => {
                const skipped = family.rows.filter((row) => row.action === 'skipped');
                const failed = family.rows.filter((row) => row.action === 'failed');
                return (
                  <li key={family.familyEnrollmentId} className="rounded-md border px-3 py-2">
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-medium">{family.familyName}</span>
                      <span className={family.message ? 'text-destructive' : 'text-muted-foreground'}>
                        {family.message ? 'Not posted' : `${plural(family.pushedCount, 'invoice', 'invoices')} posted`}
                      </span>
                    </div>
                    {family.message && <p className="mt-1 text-destructive">{family.message}</p>}
                    {family.warnings.map((warning) => (
                      <p key={warning} className="mt-1 text-amber-700">{warning}</p>
                    ))}
                    {failed.map((row) => (
                      <p key={row.invoiceId} className="mt-1 text-destructive">
                        {memoFor(row.invoiceId)}: {row.message ?? 'failed'}
                      </p>
                    ))}
                    {skipped.map((row) => (
                      <p key={row.invoiceId} className="mt-1 text-muted-foreground">
                        Skipped {memoFor(row.invoiceId)}: {row.message ?? 'not posted'}
                      </p>
                    ))}
                  </li>
                );
              })}
            </ul>
            <DialogFooter>
              <Button onClick={() => onOpenChange(false)}>Done</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Post {plural(invoices.length, 'invoice', 'invoices')}</DialogTitle>
              <DialogDescription>
                {plural(invoices.length, 'invoice', 'invoices')} across {plural(familyCount, 'family', 'families')},{' '}
                {formatCurrency(totalCents)} in all. Creates these invoices in Stripe. Sent invoices can be paid from
                the family&apos;s portal; automatic ones charge on their due date.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3">
              <Label>Collection</Label>
              <RadioGroup
                value={collectionMethod}
                onValueChange={(value) => setCollectionMethod(value as CollectionMethod)}
                aria-label="Collection"
              >
                <div className="flex items-start gap-2">
                  <RadioGroupItem value="send_invoice" id="post-collection-send" className="mt-0.5" />
                  <Label htmlFor="post-collection-send" className="font-normal">
                    Send to pay
                    <span className="block text-xs text-muted-foreground">
                      Each family pays from their SolaStack account.
                    </span>
                  </Label>
                </div>
                {offerAutomatic && (
                  <div className="flex items-start gap-2">
                    <RadioGroupItem value="charge_automatically" id="post-collection-auto" className="mt-0.5" />
                    <Label htmlFor="post-collection-auto" className="font-normal">
                      Charge automatically
                      <span className="block text-xs text-muted-foreground">
                        Charges the family&apos;s saved payment method on the due date.
                      </span>
                    </Label>
                  </div>
                )}
              </RadioGroup>

              {collectionMethod === 'charge_automatically' && (
                <div className="space-y-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                  <p>
                    Only tuition invoices from families that authorized auto-pay are charged:{' '}
                    {plural(chargeable.length, 'invoice', 'invoices')} here. The other{' '}
                    {plural(skippedUnderAutomatic.length, 'invoice', 'invoices')} selected{' '}
                    {skippedUnderAutomatic.length === 1 ? 'is' : 'are'} skipped and stay drafts; post them with
                    Send to pay.
                  </p>
                  {familiesWithoutAutopay.length > 0 && (
                    <p>
                      No auto-pay authorization, so nothing is charged for: {familiesWithoutAutopay.join(', ')}.
                    </p>
                  )}
                </div>
              )}

              {error && (
                <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{error}</span>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
                Cancel
              </Button>
              <Button onClick={handleConfirm} disabled={loading || invoices.length === 0}>
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {loading ? 'Posting...' : `Post ${plural(invoices.length, 'invoice', 'invoices')}`}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
